import React, { useState, useRef, useEffect } from "react";
import {
	InfiniteLoader,
	List,
	AutoSizer,
	CellMeasurer,
	CellMeasurerCache
} from "react-virtualized";

import { shallowEqual, useDispatch, useSelector } from "react-redux";

import newsActions from "../../redux/actions/newsActions";
import { getConvItemSelector } from "redux/selectors/cockpitSelectors";
import { Skeleton } from "@material-ui/lab";
import ClientNewsPost from "./ClientNewsPost";
import FaibrikNewsPost from "./FaibrikNewsPost";
import SnackbarStore from "pages/Cockpit/SnackbarStore";

const PAGE_SIZE = 10;

/**
 * NewsPage component to display a list of news items
 * @returns {JSX.Element} - The rendered news page component
 */
const NewsPage = () => {
	const dispatch = useDispatch();
	const selectedAssistantID = useSelector(state => state.selectedAssistantID);

	const { userLanguage, dateFormat } = useSelector(getConvItemSelector, shallowEqual);

	const [categories, setCategories] = useState([]);
	const [news, setNews] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [isLoadingRows, setIsLoadingRows] = useState({});
	const [hasMore, setHasMore] = useState(true);

	const cache = useRef(
		new CellMeasurerCache({
			fixedWidth: true,
			defaultHeight: 100
		})
	);

	const isRowLoaded = ({ index }) => !!news[index];

	const loadMoreRows = ({ startIndex, stopIndex }) => {
		// Avoid duplicate loads
		if (isLoadingRows[startIndex]) return Promise.resolve();

		setIsLoadingRows(prev => ({ ...prev, [startIndex]: true }));

		const skip = startIndex;
		const limit = PAGE_SIZE;

		return new Promise(resolve => {
			dispatch(
				newsActions.getNews(selectedAssistantID, limit, skip, res => {
					const newItems = res.news || [];
					const updatedNews = [...news];

					newItems.forEach((item, i) => {
						updatedNews[skip + i] = item;
					});

					setHasMore(prev => updatedNews.length < res.totalCount);

					setNews(updatedNews);
					setTotalCount(res.totalCount || 0);
					setIsLoadingRows(prev => {
						const updated = { ...prev };
						delete updated[startIndex];
						return updated;
					});
					resolve();
				})
			);
		});
	};

	const rowRenderer = ({ key, index, parent, style }) => {
		const item = news[index];

		const getNewsPost = measure => {
			switch (item?.storage) {
				case "catalog":
					return (
						<FaibrikNewsPost
							index={index}
							news={item}
							onLoad={measure}
							language={userLanguage}
							dateFormat={dateFormat}
							categories={categories}
						/>
					);
				case "clusterClient":
				default:
					return (
						<ClientNewsPost
							index={index}
							news={item}
							onLoad={measure}
							language={userLanguage}
							dateFormat={dateFormat}
							categories={categories}
						/>
					);
			}
		};

		return (
			<CellMeasurer
				key={key}
				cache={cache.current}
				columnIndex={0}
				parent={parent}
				rowIndex={index}
			>
				{({ measure, registerChild }) => (
					<div
						ref={registerChild}
						style={{
							...style,
							padding: "1rem",
							boxSizing: "border-box",
							borderBottom: "1px solid #eee",
							display: "flex",
							justifyContent: "center"
						}}
					>
						{item ? (
							getNewsPost(measure)
						) : (
							<Skeleton
								variant="rect"
								width="70%"
								height={200}
								style={{
									borderRadius: "8px"
								}}
							/>
						)}
					</div>
				)}
			</CellMeasurer>
		);
	};

	// Fetch news categories on component mount
	useEffect(() => {
		dispatch(
			newsActions.getNewsCategories(selectedAssistantID, res => {
				setCategories(res.categories || []);
			})
		);
	}, []);

	if (!selectedAssistantID) {
		return null;
	}

	return (
		<div
			style={{
				position: "relative"
			}}
		>
			<SnackbarStore />
			<div
				style={{
					height: "calc(100vh - 20px)"
				}}
			>
				<InfiniteLoader
					isRowLoaded={isRowLoaded}
					loadMoreRows={loadMoreRows}
					rowCount={hasMore ? news.length + PAGE_SIZE : totalCount}
				>
					{({ onRowsRendered, registerChild }) => (
						<AutoSizer>
							{({ height, width }) => (
								<List
									height={height}
									width={width}
									rowRenderer={rowRenderer}
									rowCount={hasMore ? news.length + PAGE_SIZE : totalCount}
									onRowsRendered={onRowsRendered}
									ref={registerChild}
									deferredMeasurementCache={cache.current}
									rowHeight={cache.current.rowHeight}
								/>
							)}
						</AutoSizer>
					)}
				</InfiniteLoader>
			</div>
		</div>
	);
};

export default NewsPage;
